import { HStack, Skeleton, VStack } from '@vidahealth/vida-ui';

type SelectableRowSkeletonVariant = 'filled' | 'avatar' | 'avatar_and_text';

type SelectableRowSkeletonProps = {
  /** Number of placeholder rows to render. */
  count?: number;
  /** Row height in px — match the SelectableRow height the list will render. */
  rowHeight?: number;
  /**
   * Row contents, all inside the bordered row shell:
   * - 'filled' (default): a single text stripe — for plain label rows (e.g.
   *   health conditions).
   * - 'avatar': a circular avatar placeholder only.
   * - 'avatar_and_text': a circular avatar placeholder + a text stripe —
   *   mirrors an icon + label SelectableRow (e.g. health goals).
   */
  variant?: SelectableRowSkeletonVariant;
};

// Loading placeholder for a SelectableRow list, shown while a catalog loads
// instead of a bare full-screen spinner so the screen's shape stays stable.
export function SelectableRowSkeleton({
  count = 6,
  rowHeight = 80,
  variant = 'filled',
}: SelectableRowSkeletonProps) {
  const showAvatar = variant === 'avatar' || variant === 'avatar_and_text';
  const showText = variant === 'filled' || variant === 'avatar_and_text';

  return (
    <VStack space='lg'>
      {Array.from({ length: count }).map((_, index) => (
        <HStack
          key={`row-skeleton-${index}`}
          className='items-center gap-4 rounded-vui-sm border border-outline-200 px-6'
          style={{ height: rowHeight }}
        >
          {showAvatar ? <Skeleton variant='circular' className='h-16 w-16' /> : null}
          {showText ? (
            <VStack space='md' className='flex-1'>
              <Skeleton variant='rounded' className='h-4 w-3/4' />
              <Skeleton variant='rounded' className='h-4 w-1/2' />
            </VStack>
          ) : null}
        </HStack>
      ))}
    </VStack>
  );
}
